import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Image,
  FolderOpen,
  Sparkles,
  Search,
  User,
  Coins,
  Menu,
  X,
  ScanSearch,
  LogOut,
  Settings,
  ChevronDown,
  History,
  PenLine,
  LayoutDashboard,
  BookOpen,
  Brush
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { canAccessAssets } from '@/config/access'
import clsx from 'clsx'

interface NavItem {
  path: string
  label: string
  icon: typeof Image
  children?: { path: string; label: string; icon: typeof Image }[]
}

const navItems: NavItem[] = [
  { path: '/dashboard', label: '대시보드', icon: LayoutDashboard },
  { path: '/rewrite', label: '콘텐츠 생성', icon: PenLine },
  {
    path: '/image', label: '이미지 제작', icon: Sparkles,
    children: [
      { path: '/assets', label: '에셋 라이브러리', icon: Image },
      { path: '/editor', label: '이미지 에디터', icon: Brush },
      { path: '/analyzer', label: '이미지 분석', icon: ScanSearch },
    ],
  },
  { path: '/content-projects', label: '프로젝트', icon: FolderOpen },
  { path: '/guidelines', label: '가이드라인', icon: BookOpen },
  { path: '/settings', label: '설정', icon: Settings },
]

export default function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const { user, isAuthenticated, points, logout } = useAuthStore()

  // 에셋 라이브러리 메뉴는 허용된 사용자에게만 노출
  const visibleNavItems = navItems
    .map((item) =>
      item.children
        ? { ...item, children: item.children.filter((child) => child.path !== '/assets' || canAccessAssets(user?.email)) }
        : item
    )
    .filter((item) => !item.children || item.children.length > 0)

  // 외부 클릭 시 사용자 메뉴 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    logout()
    setUserMenuOpen(false)
    navigate('/')
  }

  const isActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'))
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Global Navigation Bar */}
      <header className="sticky top-0 z-40 h-14 bg-bg-secondary/95 backdrop-blur border-b border-border-subtle">
        <nav className="h-full max-w-[1920px] mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 text-accent-gold font-bold text-xl">
            <span className="text-2xl">◆</span>
            <span className="hidden sm:inline">AI Studio</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1" ref={dropdownRef}>
            {visibleNavItems.map((item) => (
              item.children ? (
                <div key={item.path} className="relative">
                  <button
                    onClick={() => setOpenDropdown(openDropdown === item.path ? null : item.path)}
                    className={clsx(
                      'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                      isActive(item)
                        ? 'bg-bg-elevated text-accent-gold'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                    )}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                    <ChevronDown className={clsx('w-3 h-3 transition-transform', openDropdown === item.path && 'rotate-180')} />
                  </button>
                  {openDropdown === item.path && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-bg-tertiary border border-border-subtle rounded-lg shadow-lg py-1 z-50">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setOpenDropdown(null)}
                          className={clsx(
                            'flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                            location.pathname === child.path || location.pathname.startsWith(child.path + '/')
                              ? 'text-accent-gold bg-bg-elevated'
                              : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                          )}
                        >
                          <child.icon className="w-4 h-4" />
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={clsx(
                    'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive(item)
                      ? 'bg-bg-elevated text-accent-gold'
                      : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                  )}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              )
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Search */}
            <button className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-elevated rounded-lg transition-colors">
              <Search className="w-5 h-5" />
            </button>

            {/* Points */}
            {isAuthenticated && (
              <Link
                to="/points"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated rounded-lg text-sm"
              >
                <Coins className="w-4 h-4 text-accent-gold" />
                <span className="font-medium">{points.toLocaleString()}P</span>
              </Link>
            )}

            {/* User Menu */}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-1.5 hover:bg-bg-elevated rounded-lg transition-colors"
                >
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.name}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-accent-purple flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <ChevronDown className={clsx(
                    "w-4 h-4 text-text-tertiary transition-transform",
                    userMenuOpen && "rotate-180"
                  )} />
                </button>

                {/* Dropdown Menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-bg-tertiary border border-border-subtle rounded-lg shadow-lg py-2 z-50">
                    {/* User Info */}
                    <div className="px-4 py-2 border-b border-border-subtle">
                      <p className="font-medium text-sm truncate">{user?.name}</p>
                      <p className="text-xs text-text-tertiary truncate">{user?.email}</p>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <Link
                        to="/settings"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        설정
                      </Link>

                      {/* Admin Only - Login History */}
                      {user?.email === 'wizice100@gmail.com' && (
                        <Link
                          to="/login-history"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
                        >
                          <History className="w-4 h-4" />
                          접속이력
                        </Link>
                      )}
                    </div>

                    {/* Logout */}
                    <div className="border-t border-border-subtle pt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-accent-error hover:bg-bg-elevated transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        로그아웃
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="btn btn-primary text-sm"
              >
                로그인
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 text-text-secondary hover:text-text-primary"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-14 left-0 right-0 bg-bg-secondary border-b border-border-subtle">
            <div className="p-4 space-y-2">
              {visibleNavItems.map((item) => (
                item.children ? (
                  <div key={item.path}>
                    <div className="flex items-center gap-3 px-4 py-3 text-text-tertiary text-xs font-semibold uppercase">
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </div>
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={clsx(
                          'flex items-center gap-3 px-4 py-3 pl-12 rounded-lg transition-colors',
                          location.pathname === child.path
                            ? 'bg-bg-elevated text-accent-gold'
                            : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                        )}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <child.icon className="w-5 h-5" />
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={clsx(
                      'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                      location.pathname === item.path
                        ? 'bg-bg-elevated text-accent-gold'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                )
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-[1920px] mx-auto">
        <Outlet />
      </main>
    </div>
  )
}
